import { useCallback, useMemo, useState } from 'react';
import { markFolderSeen, readSeenFolders } from '@/lib/newFolders';

/**
 * Which folders this outreacher has opened, for the NEW chip badge. Reads
 * storage once per outreacher; `markSeen` updates the screen at once and
 * writes through to storage when it can. Before the outreacher is known
 * (`inviterId` null) nothing counts as seen and `ready` is false, so callers
 * don't flash NEW on folders that were opened long ago.
 */
export function useSeenFolders(inviterId: number | null): {
  ready: boolean;
  seen: ReadonlySet<string>;
  markSeen: (key: string) => void;
} {
  const stored = useMemo(() => (inviterId === null ? [] : readSeenFolders(inviterId) ?? []), [inviterId]);
  // Opened this visit, per outreacher, so it clears even when storage can't keep it.
  const [session, setSession] = useState<{ inviterId: number | null; keys: ReadonlySet<string> }>({
    inviterId: null,
    keys: new Set(),
  });
  const sessionKeys = session.inviterId === inviterId ? session.keys : null;

  const seen = useMemo(() => new Set([...stored, ...(sessionKeys ?? [])]), [stored, sessionKeys]);

  const markSeen = useCallback(
    (key: string) => {
      if (inviterId === null) return;
      setSession((prev) => {
        const keys = prev.inviterId === inviterId ? prev.keys : new Set<string>();
        return keys.has(key) ? prev : { inviterId, keys: new Set([...keys, key]) };
      });
      markFolderSeen(inviterId, key);
    },
    [inviterId],
  );

  return { ready: inviterId !== null, seen, markSeen };
}
